import React from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import './FloatingPanel.scss'

/**
 * FloatingPanel — generic dropdown/popover surface used for floating panels.
 *
 * Provides the **visual chrome only**: surface background, border, shadow, rounded corners,
 * max-height with internal scroll, and absolute positioning below its anchor on desktop.
 *
 * The caller owns the **content**: it can be a search result list, a trending list, a menu, etc.
 *
 * Currently used by:
 *  - Navbar search results dropdown (SearchWidget)
 *  - Navbar user menu, desktop (DropdownMenu)
 *  - Dropdown option list (Dropdown)
 *
 * Need a different top offset, max-height, or width? Override via `className`
 * (e.g. `className="md:top-[80px] md:max-h-[500px] md:min-w-[600px]"`).
 */
const FloatingPanel = React.forwardRef(
  (
    {
      isOpen = false,
      className = '',
      style,
      children,
      // Motion defaults: a short drop-in from just above the anchor. Override
      // per instance (e.g. to grow out of the control that opened the panel);
      // pass a `transition` inside `exit` to give the close its own timing.
      initial = { opacity: 0, y: -8, scale: 0.97 },
      animate = { opacity: 1, y: 0, scale: 1 },
      exit = { opacity: 0, y: -8, scale: 0.97 },
      transition = { type: 'spring', stiffness: 300, damping: 28, mass: 0.7 },
      ...rest
    },
    ref
  ) => (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          ref={ref}
          style={style}
          initial={initial}
          animate={animate}
          exit={exit}
          transition={transition}
          {...rest}
          className={
            // Visual surface only — no lateral padding by default, since content
            // (menu items, list rows, etc.) owns its own horizontal spacing.
            // Pass `px-*` in className if the content needs the panel to provide it.
            'floating-panel bg-surface-2 absolute z-[200] overflow-auto w-fit ' +
            'shadow-[var(--shadow-level-2)] ' +
            'border border-[length:var(--border-hairline)] border-[color:var(--floating-panel-border-color)] ' +
            'rounded-[var(--radius-sm)] ' +
            className
          }>
          {children}
        </motion.div>
      )}
    </AnimatePresence>
  )
)

FloatingPanel.displayName = 'FloatingPanel'

export default FloatingPanel
